<template>
  <div class="dispatch-waiting-page">
    <header class="waiting-header">
      <div>
        <p class="eyebrow">DERAS DISPATCH WAITING</p>
        <h1>{{ pageTitle }}</h1>
        <p>{{ headline }}</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: 'mall' })">返回发布中心</button>
        <button class="primary-btn" @click="fetchProgress">刷新进度</button>
      </div>
    </header>

    <main class="waiting-layout" v-loading="loading">
      <section class="status-card">
        <div class="pulse-ring" :class="statusTone"></div>
        <p class="eyebrow">CURRENT STATUS</p>
        <h2>{{ summary.text || 'DeRAS正在寻找机器人' }}</h2>
        <span>{{ statusDescription }}</span>
        <div class="status-actions">
          <button v-if="summary.robotName" class="primary-btn" @click="$router.push({ name: 'activeOrders' })">进入进行中任务</button>
          <button v-else class="primary-btn" @click="$router.push({ name: 'taskMarket' })">查看任务市场</button>
          <button class="ghost-btn" @click="$router.push({ name: 'mall' })">查看我的发布</button>
        </div>
      </section>

      <section class="metric-grid">
        <article><span>候选机器人</span><strong>{{ summary.candidateCount || 0 }}</strong><em>已参与派单</em></article>
        <article><span>当前机器人</span><strong>{{ summary.robotName || '等待确认' }}</strong><em>最优候选或已接单</em></article>
        <article><span>无候选扫描</span><strong>{{ summary.noCandidateCount || 0 }}</strong><em>系统仍会继续重试</em></article>
        <article><span>兜底状态</span><strong>{{ summary.fallbackAt ? '开放抢单' : '自动派单' }}</strong><em>{{ summary.lastScanAt || '等待扫描' }}</em></article>
      </section>

      <section class="content-grid">
        <article class="panel">
          <div class="section-title">
            <p class="eyebrow">TIMELINE</p>
            <h3>派单时间线</h3>
          </div>
          <div class="timeline">
            <div v-for="(item, index) in timeline" :key="`${item.type || 'step'}-${index}`" class="timeline-item">
              <i>{{ index + 1 }}</i>
              <div>
                <span>{{ item.time || '-' }} · {{ timelineTypeLabel(item.type) }}</span>
                <strong>{{ item.title || timelineTypeLabel(item.type) }}</strong>
                <p>{{ item.content || 'DeRAS正在同步派单事件。' }}</p>
              </div>
            </div>
            <p v-if="!timeline.length" class="empty-note">暂无派单时间线。</p>
          </div>
        </article>

        <article class="panel">
          <div class="section-title">
            <p class="eyebrow">CANDIDATES</p>
            <h3>候选机器人</h3>
          </div>
          <div class="candidate-list">
            <div v-for="item in dispatches" :key="item.id" class="candidate-card">
              <div>
                <strong>{{ item.robotName || `机器人 #${item.robotId}` }}</strong>
                <span>{{ dispatchStatusLabel(item.dispatchStatus) }} · 分数 {{ formatScore(item.dispatchScore) }} · {{ distanceTargetLabel(item.distanceTarget) }} {{ formatDistance(item.distanceMeters) }}</span>
              </div>
              <em>{{ item.recommendationLevel || 'NORMAL' }}</em>
              <div class="candidate-voucher">
                <span>{{ item.schedulingVoucherNo || voucherNo(item) }}</span>
                <code>{{ shortDigest(item.schedulingVoucherDigest) }}</code>
              </div>
              <p>{{ item.scoreSummary || item.dispatchReason || '暂无推荐说明' }}</p>
            </div>
            <p v-if="!dispatches.length" class="empty-note">DeRAS还没有生成候选机器人。</p>
          </div>
        </article>
      </section>

      <section v-if="showFallbackTools" class="fallback-card">
        <strong>当前暂无合适机器人，系统已开放任务市场抢单。</strong>
        <p>可以等待机器人主动接单，也可以返回发布中心调整任务类型、地点、时间或平台补助。</p>
        <div class="fallback-actions">
          <button class="primary-btn" :disabled="actionLoading" @click="redispatchTask">重新派单</button>
          <button class="ghost-btn" :disabled="actionLoading" @click="openSubsidyDialog">提高平台补助</button>
          <button class="ghost-btn" :disabled="actionLoading" @click="goEditTask">修改时间/地址</button>
          <button class="danger-btn" :disabled="actionLoading" @click="cancelTask">取消任务</button>
        </div>
      </section>
    </main>

    <el-dialog
      v-model="subsidyDialogVisible"
      width="420px"
      append-to-body
      custom-class="subsidy-dialog"
    >
      <div class="subsidy-panel">
        <p class="eyebrow">PLATFORM SUBSIDY</p>
        <h3>调整平台补助</h3>
        <p>当前阶段仍由平台提供补助，调整后 DeRAS 会取消旧邀请并重新派单。</p>
        <el-input-number
          v-model="subsidyAmount"
          :min="0.1"
          :step="0.5"
          :precision="2"
          controls-position="right"
        ></el-input-number>
        <div>
          <button class="ghost-btn" @click="subsidyDialogVisible = false">取消</button>
          <button class="primary-btn" :disabled="actionLoading" @click="submitSubsidy">确认调整</button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  cancelPublishedTask,
  getPublishedTaskDispatchProgress,
  redispatchPublishedTask,
  updatePublishedTaskSubsidy
} from '../api/task-publish'
import { getAuthUser } from '../utils/auth'

export default {
  name: 'DispatchWaiting',
  data() {
    return {
      currentUser: getAuthUser() || {},
      loading: false,
      progress: {},
      timer: null,
      actionLoading: false,
      subsidyDialogVisible: false,
      subsidyAmount: 0
    }
  },
  computed: {
    taskId() {
      return this.$route.params.taskId || this.$route.query.taskId
    },
    task() {
      return this.progress.task || {}
    },
    summary() {
      return this.progress.summary || {}
    },
    timeline() {
      return this.progress.timeline || this.summary.timeline || []
    },
    dispatches() {
      return this.progress.dispatches || []
    },
    pageTitle() {
      return this.task.title || this.task.taskName || `任务 #${this.taskId || '-'}`
    },
    headline() {
      if (this.summary.robotName) {
        if (String(this.summary.status || '').toUpperCase() === 'ACCEPTED') {
          return `机器人 ${this.summary.robotName} 已接单，可以进入进行中任务追踪服务。`
        }
        return `DeRAS 已邀请 ${this.summary.robotName}，正在等待服务者确认。`
      }
      if (this.summary.fallbackAt) return '自动派单暂未找到合适机器人，任务已开放抢单。'
      return 'DeRAS 正在根据机器人类型、SDK在线状态、距离、信誉和历史响应率寻找候选。'
    },
    statusTone() {
      if (this.summary.robotName) return 'success'
      if (this.summary.fallbackAt) return 'warning'
      return 'searching'
    },
    statusDescription() {
      if (this.summary.fallbackAt) return '你可以继续等待，也可以调整任务信息提高匹配概率。'
      if (this.summary.candidateCount) return '已向候选机器人发送邀请，等待服务者确认。'
      return '系统会持续扫描在线且空闲的机器人。'
    },
    showFallbackTools() {
      const status = String(this.summary.status || '').toUpperCase()
      return Boolean(this.summary.fallbackAt) ||
        status === 'OPEN_GRAB' ||
        (!this.summary.robotName && Number(this.summary.noCandidateCount || 0) > 0 && Number(this.summary.candidateCount || 0) === 0)
    }
  },
  created() {
    this.fetchProgress()
    this.timer = window.setInterval(this.fetchProgress, 5000)
  },
  beforeUnmount() {
    if (this.timer) window.clearInterval(this.timer)
  },
  methods: {
    async fetchProgress() {
      if (!this.taskId || !this.currentUser.id) return
      this.loading = !this.progress.task
      try {
        const response = await getPublishedTaskDispatchProgress(this.taskId, this.currentUser.id)
        if (response.data.code === 200) {
          this.progress = response.data.data || {}
        } else {
          this.$message.error(response.data.msg || '派单进度加载失败')
        }
      } catch (error) {
        this.$message.error('派单进度请求失败')
      } finally {
        this.loading = false
      }
    },
    async redispatchTask() {
      if (!this.taskId || !this.currentUser.id) return
      this.actionLoading = true
      try {
        const response = await redispatchPublishedTask(this.taskId, { userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '重新派单失败')
          return
        }
        this.$message.success('DeRAS已重新派单')
        await this.fetchProgress()
      } catch (error) {
        this.$message.error('重新派单请求失败')
      } finally {
        this.actionLoading = false
      }
    },
    openSubsidyDialog() {
      this.subsidyAmount = Number(this.task.estimatedRobocoin || 0) || 0
      this.subsidyDialogVisible = true
    },
    goEditTask() {
      this.$router.push({ name: 'mall', query: { editTaskId: this.taskId } })
    },
    async submitSubsidy() {
      if (!this.taskId || !this.currentUser.id) return
      this.actionLoading = true
      try {
        const response = await updatePublishedTaskSubsidy(this.taskId, {
          userId: this.currentUser.id,
          estimatedRobocoin: this.subsidyAmount
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '补助调整失败')
          return
        }
        this.$message.success('平台补助已调整，DeRAS正在重新派单')
        this.subsidyDialogVisible = false
        await this.fetchProgress()
      } catch (error) {
        this.$message.error('补助调整请求失败')
      } finally {
        this.actionLoading = false
      }
    },
    async cancelTask() {
      if (!this.taskId || !this.currentUser.id) return
      try {
        await this.$confirm('确认取消这个任务吗？取消后 DeRAS 会停止派单。', '取消任务', {
          confirmButtonText: '确认取消',
          cancelButtonText: '再等等',
          type: 'warning'
        })
        this.actionLoading = true
        const response = await cancelPublishedTask({
          taskId: this.taskId,
          userId: this.currentUser.id
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务取消失败')
          return
        }
        this.$message.success('任务已取消')
        this.$router.push({ name: 'mall' })
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('任务取消失败')
      } finally {
        this.actionLoading = false
      }
    },
    formatScore(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '-'
    },
    formatDistance(value) {
      const meters = Number(value)
      if (!Number.isFinite(meters) || meters <= 0) return '距离未知'
      if (meters < 1000) return `${Math.round(meters)}m`
      return `${(meters / 1000).toFixed(2)}km`
    },
    distanceTargetLabel(value) {
      if (value === 'PICKUP_POINT') return '距上车点'
      if (value === 'SERVICE_ADDRESS') return '距服务点'
      return '距目标点'
    },
    dispatchStatusLabel(value) {
      const map = {
        PENDING: '等待确认',
        ACCEPTED: '已接受',
        REJECTED: '已拒绝',
        EXPIRED: '已超时',
        CANCELLED: '已取消'
      }
      return map[value] || value || '未知'
    },
    voucherNo(item) {
      return item && item.taskId && item.id ? `SV-${item.taskId}-${item.id}` : 'SV-待生成'
    },
    shortDigest(value) {
      if (!value) return 'digest pending'
      const text = String(value)
      return text.length > 18 ? `${text.slice(0, 18)}...` : text
    },
    timelineTypeLabel(value) {
      const map = {
        SCHEDULING_VOUCHER_CREATED: '调度凭证生成',
        SCHEDULING_VOUCHER_ACCEPTED: '调度凭证确认',
        ACCEPT_BLOCKED: '接受派单被阻断',
        CREATED: '任务发布',
        SCAN: '派单扫描',
        INVITED: '发送邀请',
        ACCEPTED: '已接单',
        REJECTED: '已拒绝',
        EXPIRED: '已超时',
        OPEN_GRAB: '开放抢单',
        OPEN_GRAB_FALLBACK: '开放抢单'
      }
      return map[value] || value || '派单事件'
    }
  }
}
</script>

<style scoped>
.dispatch-waiting-page {
  min-height: 100vh;
  padding: 30px;
  color: #f6fbff;
  background:
    radial-gradient(circle at 16% 0%, rgba(143, 241, 199, 0.18), transparent 26%),
    radial-gradient(circle at 90% 8%, rgba(255, 208, 138, 0.16), transparent 24%),
    linear-gradient(135deg, #06111e, #071827 48%, #050b15);
}

.waiting-header,
.status-card,
.metric-grid article,
.panel,
.fallback-card {
  border: 1px solid rgba(143, 241, 199, 0.14);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.22);
}

.waiting-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 24px;
  border-radius: 24px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #8ff1c7;
  font-size: 12px;
  letter-spacing: 0.14em;
}

.waiting-header h1 {
  margin: 0;
  font-size: 44px;
}

.waiting-header p,
.status-card span,
.timeline p,
.candidate-card p,
.fallback-card p {
  color: rgba(246, 251, 255, 0.68);
}

.header-actions,
.status-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.primary-btn,
.ghost-btn {
  height: 40px;
  min-width: 116px;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
}

.primary-btn {
  border: none;
  color: #03101d;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.ghost-btn {
  color: #e8fbff;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
}

.status-card {
  position: relative;
  padding: 28px;
  border-radius: 24px;
  margin-bottom: 16px;
  overflow: hidden;
}

.pulse-ring {
  position: absolute;
  right: 28px;
  top: 28px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 12px solid rgba(143, 241, 199, 0.18);
  box-shadow: inset 0 0 0 12px rgba(143, 241, 199, 0.22);
}

.pulse-ring.warning {
  border-color: rgba(255, 208, 138, 0.26);
  box-shadow: inset 0 0 0 12px rgba(255, 208, 138, 0.22);
}

.pulse-ring.success {
  border-color: rgba(143, 241, 199, 0.36);
  box-shadow: inset 0 0 0 12px rgba(143, 241, 199, 0.32);
}

.status-card h2 {
  margin: 0 0 8px;
  font-size: 34px;
}

.status-actions {
  margin-top: 18px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.metric-grid article {
  border-radius: 20px;
  padding: 18px;
}

.metric-grid span,
.metric-grid em,
.candidate-card span,
.timeline span {
  display: block;
  color: rgba(246, 251, 255, 0.58);
  font-style: normal;
}

.metric-grid strong {
  display: block;
  margin: 8px 0;
  font-size: 28px;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 16px;
}

.panel,
.fallback-card {
  border-radius: 24px;
  padding: 20px;
}

.section-title h3 {
  margin: 0 0 16px;
}

.timeline,
.candidate-list {
  display: grid;
  gap: 12px;
}

.timeline-item,
.candidate-card {
  display: grid;
  gap: 12px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(5, 13, 23, 0.62);
}

.timeline-item {
  grid-template-columns: 34px minmax(0, 1fr);
}

.timeline-item i {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #03101d;
  background: #8ff1c7;
  font-style: normal;
  font-weight: 900;
}

.candidate-card {
  grid-template-columns: minmax(0, 1fr) auto;
}

.candidate-card em {
  color: #ffd08a;
  font-style: normal;
  font-weight: 900;
}

.candidate-card p {
  grid-column: 1 / -1;
  margin: 0;
}

.candidate-voucher {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: -4px;
}

.candidate-voucher span,
.candidate-voucher code {
  padding: 6px 9px;
  border-radius: 999px;
  font-size: 12px;
}

.candidate-voucher span {
  color: #03101d;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
  font-weight: 900;
}

.candidate-voucher code {
  color: #9bd7ff;
  background: rgba(7, 15, 27, 0.72);
  border: 1px solid rgba(119, 184, 255, 0.18);
}

.fallback-card {
  margin-top: 16px;
  border-color: rgba(255, 208, 138, 0.28);
}

.fallback-actions,
.subsidy-panel > div:last-child {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.danger-btn {
  height: 40px;
  min-width: 116px;
  border: 1px solid rgba(255, 118, 118, 0.48);
  border-radius: 999px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(235, 72, 88, 0.86);
}

.primary-btn:disabled,
.ghost-btn:disabled,
.danger-btn:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

:deep(.subsidy-dialog) {
  overflow: hidden;
  border-radius: 24px;
  background: #081827;
}

:deep(.subsidy-dialog .el-dialog__header) {
  display: none;
}

:deep(.subsidy-dialog .el-dialog__body) {
  padding: 0;
}

.subsidy-panel {
  display: grid;
  gap: 12px;
  padding: 24px;
  color: #f6fbff;
  background:
    radial-gradient(circle at 0% 0%, rgba(143, 241, 199, 0.16), transparent 30%),
    linear-gradient(135deg, #06111e, #081827);
}

.subsidy-panel h3 {
  margin: 0;
  font-size: 24px;
}

.subsidy-panel p {
  margin: 0;
  color: rgba(246, 251, 255, 0.68);
  line-height: 1.7;
}

.subsidy-panel :deep(.el-input-number) {
  width: 100%;
}

@media (max-width: 980px) {
  .waiting-header,
  .content-grid {
    grid-template-columns: 1fr;
  }

  .waiting-header {
    flex-direction: column;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .dispatch-waiting-page {
    padding: 18px;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }
}
</style>
